/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Link, Tooltip, Typography } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { StatCardV2 } from '../../../components/v2';
import {
  EMPTY_KPIS,
  buildCategoryCardItems,
  formatBRL,
  formatPercent,
  getCategoryGridSize,
} from './constants';

const TOTAL_KEY = '__TOTAL__';

const SUMMARY_CARDS = [
  {
    key: TOTAL_KEY,
    kpiKey: 'totalGasto',
    label: 'Total Gasto',
    icon: <AttachMoneyIcon />,
    color: 'primary',
    filterable: false,
    format: formatBRL,
    valueFontSize: '1.5rem',
  },
  {
    key: 'viaAporte',
    kpiKey: 'viaAporte',
    label: 'Via Aporte',
    icon: <AccountBalanceWalletIcon />,
    color: 'info',
    filterable: false,
    format: formatBRL,
  },
  {
    key: 'viaCartao',
    kpiKey: 'viaCartao',
    label: 'Via Cartão',
    icon: <CreditCardIcon />,
    color: 'success',
    filterable: false,
    format: formatBRL,
  },
  {
    key: 'quantidadeNotas',
    kpiKey: 'quantidadeNotas',
    label: 'Qtd. Notas',
    icon: <ReceiptLongIcon />,
    color: 'default',
    filterable: false,
    format: (v) => String(v ?? 0),
  },
  {
    key: 'ticketMedio',
    kpiKey: 'ticketMedio',
    label: 'Ticket Médio',
    icon: <ShoppingCartOutlinedIcon />,
    color: 'default',
    filterable: false,
    format: formatBRL,
    nullHint: 'Indisponível sem notas no período',
  },
  {
    key: 'percentualInspecao',
    kpiKey: 'percentualInspecao',
    label: '% Inspeção',
    icon: <FactCheckOutlinedIcon />,
    color: 'warning',
    filterable: false,
    format: formatPercent,
    nullHint: 'Sem gastos de inspeção no período',
  },
];

const KpiStrip = ({
  kpis,
  categoryKpis,
  loading,
  activeCategoria,
  onSelectCategoria,
}) => {
  const [showZeroCategories, setShowZeroCategories] = useState(false);
  const data = kpis || EMPTY_KPIS;
  const categoryData = categoryKpis || data;

  const categoryItems = useMemo(
    () => buildCategoryCardItems(categoryData, {
      hideZero: !showZeroCategories,
      sort: 'desc',
    }),
    [categoryData, showZeroCategories],
  );

  const categoryGridSize = useMemo(
    () => getCategoryGridSize(categoryItems.length),
    [categoryItems.length],
  );

  const hasHiddenZeroCategories = useMemo(
    () => buildCategoryCardItems(categoryData, { hideZero: true }).length
      < buildCategoryCardItems(categoryData, { hideZero: false }).length,
    [categoryData],
  );

  const handleClick = (label) => {
    if (!onSelectCategoria) return;
    onSelectCategoria(activeCategoria === label ? null : label);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {SUMMARY_CARDS.map((card) => {
          const rawValue = data[card.kpiKey];
          const isNullMetric = rawValue == null && card.nullHint;
          const displayValue = isNullMetric ? '—' : card.format(rawValue ?? 0);

          const cardNode = (
            <StatCardV2
              label={card.label}
              value={displayValue}
              icon={card.icon}
              color={card.color}
              loading={loading}
              valueFontSize={card.valueFontSize}
            />
          );

          return (
            <Grid key={card.key} size={{ xs: 6, sm: 4, md: 2 }}>
              {isNullMetric ? (
                <Tooltip title={card.nullHint} arrow placement="top">
                  <span>{cardNode}</span>
                </Tooltip>
              ) : (
                cardNode
              )}
            </Grid>
          );
        })}
      </Grid>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mt: 2,
          mb: 1,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, textTransform: 'uppercase' }}
        >
          Por categoria — clique para filtrar o painel
        </Typography>
        {hasHiddenZeroCategories && (
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            onClick={() => setShowZeroCategories((prev) => !prev)}
            sx={{ fontWeight: 600, cursor: 'pointer' }}
          >
            {showZeroCategories ? 'Ocultar zeradas' : 'Mostrar zeradas'}
          </Link>
        )}
      </Box>

      {!loading && categoryItems.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Nenhuma categoria com gasto no período selecionado.
        </Typography>
      ) : (
        <Grid container spacing={1}>
          {categoryItems.map((item) => {
            const isActive = activeCategoria === item.label;
            const percentLabel = item.valor > 0 ? formatPercent(item.percentual) : null;
            const cardLabel = percentLabel ? `${item.label} · ${percentLabel}` : item.label;

            return (
              <Grid key={item.enum} size={categoryGridSize}>
                <StatCardV2
                  label={cardLabel}
                  value={formatBRL(item.valor)}
                  color={item.color}
                  loading={loading}
                  active={isActive}
                  activeLabel={isActive ? 'Filtro ativo — clique para remover' : undefined}
                  valueFontSize="1.1rem"
                  onClick={() => handleClick(item.label)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

KpiStrip.propTypes = {
  kpis: PropTypes.object,
  categoryKpis: PropTypes.object,
  loading: PropTypes.bool,
  activeCategoria: PropTypes.string,
  onSelectCategoria: PropTypes.func,
};

KpiStrip.defaultProps = {
  kpis: EMPTY_KPIS,
  categoryKpis: null,
  loading: false,
  activeCategoria: null,
  onSelectCategoria: null,
};

export default KpiStrip;
